// dependencies
import React, { useEffect, useRef, useState } from "react";

import Button from "../../../../components/Button";
import SelectInput from "../../../../components/Inputs/SelectInput";
// config
import Config from "./Config";
// components
import Form from "./Form";
import QueryButton from "./QueryButton";
import Summary from "./Summary";

import useReportStore from "../../pages/Report/store";
import { canCreateNewEmployeeReport } from "../../pages/Report/utils";

// schema
import { FieldCategory, FieldFilter, FilterComparison } from "../../schemas/report";

// store
import useQueryBuilderStore, { SelectInputValue } from "./store";

import { useRbacPrimaryRolePermissions } from "../../../../../react/api/hooks/defs/rbac";

// styles
import css from "./QueryBuilder.module.scss";

const canCreateNewReviewReport = (permissions) => {
	if (!permissions) return false;

	return permissions.includes("report_analytics:createReport");
};

const Builder = () => {
	const [report] = useReportStore((state) => [state.report]);

	const [filterBuilder, setFilterBuilder] = useState<FieldFilter[]>([]);
	const summaryRef = useRef<HTMLDivElement>(null);
	const builderRef = useRef<HTMLDivElement>(null);

	const { data: permissions } = useRbacPrimaryRolePermissions("report_analytics");

	const permissionToCreateReviewReport = canCreateNewReviewReport(permissions) || false;
	const permissionToCreateEmployeeReport = canCreateNewEmployeeReport(permissions) || false;
	const userHasAdminPermissions =
		permissionToCreateReviewReport || (permissionToCreateEmployeeReport && report?.type === "employeeReport");
	const fieldTypeOptions = Config.categoryOptions.filter((option) =>
		Config.allowedFieldTypes[report?.type || "employeeReport"].includes(option.value)
	);

	const [filterUpdate, selectedFilterGroup, setSelectedFilterGroup, setFilterUpdateGroup, handleRemoveFilterGroup] =
		useQueryBuilderStore((state) => [
			state.filterUpdate,
			state.selectedFilterGroup,
			state.setSelectedFilterGroup,
			state.setFilterUpdateGroup,
			state.handleRemoveFilterGroup,
		]);

	useEffect(() => {
		if (
			userHasAdminPermissions &&
			selectedFilterGroup !== null &&
			selectedFilterGroup !== undefined &&
			filterUpdate[selectedFilterGroup] !== null &&
			filterUpdate[selectedFilterGroup] !== undefined
		) {
			const newFilterBuilder = [...filterUpdate[selectedFilterGroup]];
			setFilterBuilder(newFilterBuilder);
		} else {
			if (fieldTypeOptions.length === 1) {
				setFilterBuilder([
					{
						field: "",
						value: "",
						type: "",
						comparison: FilterComparison.Null,
						category: fieldTypeOptions[0].value as FieldCategory,
					},
				]);
			} else {
				setFilterBuilder([]);
				setSelectedFilterGroup(null);
			}
		}
	}, [selectedFilterGroup]);

	const setFilters = () => {
		if (userHasAdminPermissions) {
			setFilterUpdateGroup(selectedFilterGroup, [...filterBuilder]);
			setFilterBuilder([]);
			setSelectedFilterGroup(null);
		}
	};

	const isFilterIncomplete = (): boolean => {
		return (
			!filterBuilder.length ||
			filterBuilder.some((filter) => {
				return (
					!filter.field ||
					!filter.field.length ||
					filter.value === "" ||
					filter.value?.["length"] === 0 ||
					!filter.comparison ||
					!filter.category
				);
			})
		);
	};

	const isFieldSelected: boolean = (filterBuilder.length && !!filterBuilder[0] && !!filterBuilder[0].field) || false;

	const selectedCategory = filterBuilder[0]?.category || null;

	const handleClear = (filterIndex: number) => {
		const newFilterBuilder = [...filterBuilder];
		newFilterBuilder.splice(filterIndex, 1);
		setFilterBuilder(newFilterBuilder);
	};

	const handleChangeCategory = (category: SelectInputValue<FieldCategory>) => {
		if (userHasAdminPermissions) {
			const newFilterBuilder = [
				{
					field: "",
					value: "",
					type: "",
					comparison: FilterComparison.Null,
					category: category.value,
				},
			];
			setFilterBuilder(newFilterBuilder);
		}
	};

	const addNewOrFilter = () => {
		const newFilterBuilder = [
			...filterBuilder,
			{
				category: selectedCategory,
				field: filterBuilder[0].field,
				comparison: filterBuilder[0].comparison,
				type: filterBuilder[0].type,
				value: "",
			},
		];
		setFilterBuilder(newFilterBuilder);
	};

	const addNewAndFilterGroup = () => {
		setFilterBuilder([]);
		setSelectedFilterGroup(null);
	};

	const coordinateFilterRemoval = (groupIndex: number) => {
		handleRemoveFilterGroup(groupIndex);
		if (groupIndex === selectedFilterGroup) {
			setFilterBuilder([]);
		}
		setSelectedFilterGroup(null);
	};

	// Click outside of builder/summary to "reset" query builder to add new filter state
	const handleClickQueryBuilder = (event) => {
		if (
			summaryRef.current &&
			!summaryRef.current.contains(event.target) &&
			builderRef.current &&
			!builderRef.current.contains(event.target)
		) {
			addNewAndFilterGroup();
		}
	};

	return (
		<div
			data-testid="add-and"
			onClick={handleClickQueryBuilder}
			className={
				userHasAdminPermissions
					? css.queryBuilderContainer
					: `${css.queryBuilderContainer} ${css.disabledForManagers}`
			}
		>
			<div ref={builderRef} className={css.controlPanelContainer}>
				<div className={css.conditionTitle}>Select Field Type</div>
				<div data-testid="builder-input" className={css.categoryInput}>
					<SelectInput
						setFirstResult={false}
						results={fieldTypeOptions.map(({ value, label }) => ({ value, label }))}
						onChange={handleChangeCategory}
						value={selectedCategory}
						disabled={!userHasAdminPermissions}
					/>
				</div>
				{selectedCategory && userHasAdminPermissions ? (
					<>
						<div className={css.conditionTitle}>
							{filterUpdate[selectedFilterGroup]?.[0].field ? "Edit Condition" : "Add Condition"}
							<div data-testid="clear-or" onClick={() => handleClear(0)} className={css.clearButton}>
								Clear
							</div>
						</div>

						<Form
							handleClear={handleClear}
							filterBuilder={filterBuilder}
							updateFilterBuilder={setFilterBuilder}
						/>

						{isFieldSelected && (
							<div className={css.buttonsContainer}>
								<div className={css.buttonContainer}>
									<QueryButton
										color="green"
										disabled={isFilterIncomplete()}
										text="OR"
										onClick={addNewOrFilter}
										type="add"
									/>
								</div>
							</div>
						)}
					</>
				) : null}
				<div className={css.mainButtonsContainer}>
					<Button
						className={css.mainButton}
						onClick={setFilters}
						disabled={!userHasAdminPermissions || isFilterIncomplete()}
						color="blue"
					>
						{selectedFilterGroup !== null &&
						userHasAdminPermissions &&
						filterUpdate[selectedFilterGroup]?.length
							? "Update"
							: "Add"}{" "}
						Filter
					</Button>
				</div>
			</div>
			<div ref={summaryRef} className={css.querySummaryContainer}>
				<Summary removeFilter={coordinateFilterRemoval} disabledForManagers={!userHasAdminPermissions} />
			</div>
		</div>
	);
};

export default Builder;
